import React from 'react';
import PublicLayout from '../components/PublicLayout';
import { Shield, Lock, Eye, Database, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <PublicLayout>
      <div className="public-page">
        {/* Hero Section */}
        <div className="public-page-header">
          <h1>Privacy Policy</h1>
          <p>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>

        <div className="public-page-content">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="dashboard-card mb-8">
              <div className="flex items-start gap-4">
                <Shield className="w-12 h-12 flex-shrink-0" style={{color: 'var(--gold)'}} />
                <div>
                  <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '2rem', marginBottom: '1rem'}}>Our Commitment to Privacy</h2>
                  <p style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    At Success Club, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Information We Collect */}
            <div className="dashboard-card mb-8">
              <div className="flex items-start gap-4 mb-4">
                <Database className="w-10 h-10 flex-shrink-0" style={{color: 'var(--gold)'}} />
                <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '2rem'}}>Information We Collect</h2>
              </div>
              <div className="space-y-4" style={{paddingLeft: '3.5rem'}}>
                <div>
                  <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.5rem', marginBottom: '0.5rem'}}>Personal Information</h3>
                  <p style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    We collect information you provide directly, including your name, email address, phone number, company information, and payment details when you create an account or make a purchase.
                  </p>
                </div>
                <div>
                  <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.5rem', marginBottom: '0.5rem'}}>Usage Information</h3>
                  <p style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    We automatically collect information about your interactions with our platform, including pages visited, features used, events attended, and challenges completed.
                  </p>
                </div>
                <div>
                  <h3 style={{fontFamily: 'var(--font-heading)', fontSize: '1.5rem', marginBottom: '0.5rem'}}>Device & Browser Information</h3>
                  <p style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    We collect technical information such as your IP address, browser type, operating system, and device identifiers to improve our services and ensure security.
                  </p>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="dashboard-card mb-8">
              <div className="flex items-start gap-4 mb-4">
                <FileText className="w-10 h-10 flex-shrink-0" style={{color: 'var(--gold)'}} />
                <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '2rem'}}>How We Use Your Information</h2>
              </div>
              <div style={{paddingLeft: '3.5rem'}}>
                <ul className="space-y-3">
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Provide Services:</strong> To create and manage your account, process payments, and deliver the features you've subscribed to.
                  </li>
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Improve Platform:</strong> To analyze usage patterns, identify bugs, and enhance user experience.
                  </li>
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Communication:</strong> To send you event notifications, platform updates, and relevant marketing communications (you can opt out anytime).
                  </li>
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Security:</strong> To detect and prevent fraud, abuse, and security threats.
                  </li>
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.
                  </li>
                </ul>
              </div>
            </div>

            {/* Data Sharing */}
            <div className="dashboard-card mb-8">
              <div className="flex items-start gap-4 mb-4">
                <Eye className="w-10 h-10 flex-shrink-0" style={{color: 'var(--gold)'}} />
                <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '2rem'}}>How We Share Your Information</h2>
              </div>
              <div style={{paddingLeft: '3.5rem'}}>
                <p style={{color: 'var(--muted)', lineHeight: '1.8', marginBottom: '1rem'}}>
                  We do not sell your personal information. We may share your information in the following circumstances:
                </p>
                <ul className="space-y-3">
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>With Your Consent:</strong> When you explicitly agree to share information.
                  </li>
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Service Providers:</strong> With trusted third-party vendors who help us operate our platform (payment processors, email services, hosting providers).
                  </li>
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Community Members:</strong> Your profile information and activity may be visible to other Success Club members as part of the networking experience.
                  </li>
                  <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                    <strong>Legal Requirements:</strong> When required by law or to protect our rights and safety.
                  </li>
                </ul>
              </div>
            </div>

            {/* Data Security */}
            <div className="dashboard-card mb-8">
              <div className="flex items-start gap-4 mb-4">
                <Lock className="w-10 h-10 flex-shrink-0" style={{color: 'var(--gold)'}} />
                <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '2rem'}}>Data Security</h2>
              </div>
              <div style={{paddingLeft: '3.5rem'}}>
                <p style={{color: 'var(--muted)', lineHeight: '1.8'}}>
                  We implement industry-standard security measures including encryption, secure authentication, regular security audits, and access controls to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>
            </div>

            {/* Your Rights */}
            <div className="dashboard-card mb-8">
              <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '2rem', marginBottom: '1rem'}}>Your Privacy Rights</h2>
              <p style={{color: 'var(--muted)', lineHeight: '1.8', marginBottom: '1rem'}}>You have the right to:</p>
              <ul className="space-y-2" style={{paddingLeft: '1.5rem'}}>
                <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>Access and review your personal information</li>
                <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>Update or correct your information</li>
                <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>Delete your account and associated data</li>
                <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>Export your data</li>
                <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>Opt out of marketing communications</li>
                <li style={{color: 'var(--muted)', lineHeight: '1.8'}}>Object to certain data processing activities</li>
              </ul>
              <p style={{color: 'var(--muted)', lineHeight: '1.8', marginTop: '1rem'}}>
                To exercise these rights, please contact us at <a href="mailto:privacy@oursuccessclub.com" style={{color: 'var(--gold)', fontWeight: 600}}>privacy@oursuccessclub.com</a>
              </p>
            </div>

            {/* Contact */}
            <div className="text-center rounded-2xl p-12" style={{background: 'var(--dark-bg)', color: '#fff'}}>
              <h2 className="mb-4" style={{color: '#fff', fontFamily: 'var(--font-heading)', fontSize: '2.5rem'}}>Questions About Privacy?</h2>
              <p className="text-lg mb-6 max-w-2xl mx-auto" style={{color: 'rgba(255,255,255,0.8)'}}>
                If you have any questions or concerns about this Privacy Policy, please contact us:
              </p>
              <a 
                href="mailto:privacy@oursuccessclub.com"
                className="inline-block bg-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors no-underline"
                style={{color: '#1A1A1A'}}
              >
                privacy@oursuccessclub.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PrivacyPolicy;